import { observer } from 'mobx-react';
import classnames from 'classnames';
import React, { MutableRefObject, useState } from 'react';
import { IEmailTemplateField } from '../../../../../../api/email-templates.dao';
import { Popover, PopoverPosition } from '../../../../../global/popover/popover';
import { SearchInput } from '../../../../../global/search-input/search-input';

import './email-templates-dropdown.scss';
import { useIntl } from 'react-intl';
import { EMAIL_TEMPLATES_LIST_EMPTY, LOADING_EMAIL_TEMPLATES } from '../../../../../_email-templates/email-templates.text';
import Text from '../../../../../global/text/text';
import { EMPTY_SEARCH_RESULT } from '../../../../../../constants/common-labels.constants';

export interface IEmailTemplatesListDropdownProps {
    emailTemplatesList: IEmailTemplateField[];
    isOpened: boolean;
    itemRef: MutableRefObject<HTMLElement>;
    closeEmailTemplatesDropdown: () => void;
    selectTemplateFromList: (templateId: string) => Promise<void>;
    loadingEmailTemplates?: boolean;
    selectedEmailTemplate: IEmailTemplateField;
    position: PopoverPosition;
    customLeft?: number;
    customTop?: number;
}

export const EmailTemplatesListDropdown = observer(({
    closeEmailTemplatesDropdown,
    selectTemplateFromList,
    emailTemplatesList,
    isOpened,
    itemRef,
    loadingEmailTemplates,
    selectedEmailTemplate,
    position,
    customLeft,
    customTop,
}: IEmailTemplatesListDropdownProps) => {

    const PopoverBody = () => {
        const [search, setSearch] = useState<string>('');
        const intl = useIntl();

        const handleSearch = (e: React.ChangeEvent<HTMLInputElement>, v: string): void => {
            e.preventDefault();
            setSearch(v);
        };

        const getFilteredTemplates = (): IEmailTemplateField[] => {
            if (!search) return emailTemplatesList;
            return emailTemplatesList.filter(x => x.name.toLowerCase().includes(search.trim().toLowerCase()));
        };

        const filteredTemplates = getFilteredTemplates();

        if (loadingEmailTemplates) {
            return <div className='loading-email-templates'>
                {intl.formatMessage(LOADING_EMAIL_TEMPLATES)}
            </div>;
        } else if (!loadingEmailTemplates && !emailTemplatesList.length) {
            return <div className='loading-email-templates'>
                {intl.formatMessage(EMAIL_TEMPLATES_LIST_EMPTY)}
            </div>;
        } else if (!loadingEmailTemplates && !filteredTemplates.length) {
            return <div className='loading-email-templates'>
                {intl.formatMessage(EMPTY_SEARCH_RESULT)}
            </div>;
        } else {
            return <div className='email-templates-dropdown'>
                <div className='email-templates-dropdown__search-input-wrapper'>
                    <SearchInput value={search} onChange={handleSearch} />
                </div>
                <div className='w-100' onClick={e => e.stopPropagation()}>
                    <ul className='email-templates-dropdown__list'>
                        {filteredTemplates.map(template => {
                            const isTemplateSelected = selectedEmailTemplate?.id === template.id;
                            return <li
                                key={template.id}
                                className={classnames('email-templates-dropdown__list-item', {
                                    'email-templates-dropdown__list-item--selected': isTemplateSelected
                                })}
                                onClick={() => {
                                    selectTemplateFromList(template.id);
                                    closeEmailTemplatesDropdown();
                                }}
                            >
                                <Text
                                    withEllipsis
                                    className='email-templates-dropdown__list-item-name'
                                    message={template.name}
                                />
                                <Text
                                    withEllipsis
                                    className='email-templates-dropdown__list-item-description'
                                    message={template.description}
                                />
                            </li>;
                        })}
                    </ul>
                </div>
            </div>;
        }
    };

    return (
        <Popover
            isOpen={isOpened}
            buttonRef={itemRef}
            onClickOutside={closeEmailTemplatesDropdown}
            customWidth={300}
            padding={0}
            align='start'
            customLeft={customLeft}
            customTop={customTop}
            position={position}
        >
            <PopoverBody />
        </Popover>
    );
});
